'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { GlassBox } from '@/components/custom/box'
import { Button } from '@sarkiai/ui'
import { Contact, Bot, Building2, ArrowRight } from 'lucide-react'
import Link from 'next/link'
import { useTranslations } from '@/components/I18nProvider'

const services = [
  {
    id: 'enterprise',
    titleKey: 'enterprise.agent_title',
    descriptionKey: 'enterprise.description',
    icon: Building2,
    href: '/enterprise'
  },
  {
    id: 'sarkiai',
    titleKey: 'sarkiai.title',
    descriptionKey: 'sarkiai.description',
    icon: Bot,
    href: '/sarkiai'
  },
  {
    id: 'vcard',
    titleKey: 'vcard.title',
    descriptionKey: 'vcard.description',
    icon: Contact,
    href: '/vcard'
  }
]

export function ServicesSection() {
  const t = useTranslations('services')

  return (
    <section className="py-20 relative overflow-hidden px-4 lg:px-12">
      {/* Background Decorations */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full -z-10">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-black/5 dark:bg-white/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-black/5 dark:bg-white/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl md:text-4xl uppercase tracking-tight mb-6"
          >
            {t('sectionTitle')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-muted-foreground leading-relaxed"
          >
            {t('sectionSubtitle')}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              className="h-full"
              viewport={{ once: true }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <GlassBox className="h-full flex flex-col p-10 transition-all duration-500 border border-black/5 dark:border-white/10 hover:border-black/20 dark:hover:border-white/20 hover:shadow-2xl hover:shadow-black/5 rounded-[2.5rem] group">
                <div className="w-16 h-16 rounded-2xl bg-transparent border border-black/10 dark:border-white/20 flex items-center justify-center mb-8 shadow-xl shadow-black/5 group-hover:scale-110 transition-transform duration-500">
                  <service.icon className="w-8 h-8 text-black dark:text-white" />
                </div>

                <h3 className="text-2xl uppercase tracking-tight mb-4">
                  {t(service.titleKey)}
                </h3>

                <p className="text-muted-foreground mb-8 text-sm leading-relaxed line-clamp-3">
                  {t(service.descriptionKey)}
                </p>

                <div className="mt-auto">
                  <Button asChild variant="ghost" className="rounded-2xl px-6 h-10 uppercase tracking-widest text-xs border border-transparent hover:bg-black hover:dark:bg-black hover:border-black dark:hover:border-white transition-all">
                    <Link href={service.href} className="flex items-center gap-2">
                      {t('learnMore')}
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </Button>
                </div>
              </GlassBox>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
